import { Loader2, Trash2, Upload } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Button } from "../../../../components/ui/common/button";
import { errorView } from "../../../../helpers/ErrorHandler";
import { convertImageToPng } from "../../../../utils/imageUtils";

const MAX_LOGO_BYTES = 2 * 1024 * 1024;

interface CompanyLogoUploadProps {
  /** Persisted logo (signed URL) to show as the starting image — edit flow only. */
  initialImageUrl?: string | null;
  /** Fires with the PNG-converted file (or the original if conversion fails). */
  onUpload: (file: File) => void;
  /** Fires when the shown logo (persisted or just-picked) is removed. */
  onRemove?: () => void;
}

/**
 * Company logo drop zone: a dashed card ("Upload logo — PNG, JPG, SVG · max
 * 2 MB") that turns into a preview with Replace / Remove once an image is
 * set. Keeps the legacy `NewImageUploader` behaviour the company screens
 * relied on — the picked image is converted to PNG (`convertImageToPng`)
 * before it's handed to the parent, with the original file as fallback if
 * conversion fails — and enforces the 2 MB limit the card advertises.
 */
const CompanyLogoUpload = ({ initialImageUrl, onUpload, onRemove }: CompanyLogoUploadProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(initialImageUrl ?? null);
  const [isConverting, setIsConverting] = useState(false);
  const isObjectUrlRef = useRef(false);

  useEffect(() => {
    setPreviewUrl(prev => {
      if (isObjectUrlRef.current && prev) URL.revokeObjectURL(prev);
      return initialImageUrl ?? null;
    });
    isObjectUrlRef.current = false;
  }, [initialImageUrl]);

  // Revoke any locally-created object URL on unmount so repeated picks don't leak blob URLs.
  useEffect(() => {
    return () => {
      if (isObjectUrlRef.current && previewUrl) URL.revokeObjectURL(previewUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showFile = (file: File) => {
    setPreviewUrl(prev => {
      if (isObjectUrlRef.current && prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
    isObjectUrlRef.current = true;
    onUpload(file);
  };

  const handleSelect = async (file: File) => {
    if (file.size > MAX_LOGO_BYTES) {
      errorView("Logo must be 2 MB or smaller");
      return;
    }
    setIsConverting(true);
    try {
      showFile(await convertImageToPng(file));
    } catch {
      showFile(file);
    } finally {
      setIsConverting(false);
    }
  };

  const handleRemove = () => {
    setPreviewUrl(prev => {
      if (isObjectUrlRef.current && prev) URL.revokeObjectURL(prev);
      return null;
    });
    isObjectUrlRef.current = false;
    onRemove?.();
  };

  const openPicker = () => inputRef.current?.click();

  return (
    <div className="tw-rounded-lg tw-border tw-border-dashed tw-border-border tw-bg-white">
      {previewUrl ? (
        <div className="tw-flex tw-flex-col tw-items-center tw-gap-4 tw-p-5">
          <img
            src={previewUrl}
            alt="Company logo"
            className="tw-h-24 tw-max-w-full tw-object-contain"
          />
          <div className="tw-flex tw-gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={isConverting}
              onClick={openPicker}
            >
              {isConverting ? (
                <Loader2 className="tw-h-4 tw-w-4 tw-animate-spin" />
              ) : (
                <Upload className="tw-h-4 tw-w-4" />
              )}
              Replace
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              title="Remove logo"
              onClick={handleRemove}
            >
              <Trash2 className="tw-h-4 tw-w-4" />
            </Button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          disabled={isConverting}
          onClick={openPicker}
          className="tw-flex tw-w-full tw-flex-col tw-items-center tw-gap-1.5 tw-px-4 tw-py-8 tw-text-center disabled:tw-opacity-60"
        >
          {isConverting ? (
            <Loader2 className="tw-h-5 tw-w-5 tw-animate-spin tw-text-primary" />
          ) : (
            <Upload className="tw-h-5 tw-w-5 tw-text-primary" />
          )}
          <span className="tw-text-base tw-font-medium tw-text-foreground">
            {isConverting ? "Converting..." : "Upload logo"}
          </span>
          <span className="tw-font-mono tw-text-xs tw-text-muted-foreground">
            PNG, JPG, SVG · max 2 MB
          </span>
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/png, image/jpeg, image/jpg, image/svg+xml"
        className="tw-hidden"
        data-testid="company-logo-input"
        onChange={e => {
          const file = e.target.files?.[0];
          if (file) void handleSelect(file);
          e.target.value = "";
        }}
      />
    </div>
  );
};

export default CompanyLogoUpload;
